import apiService from "@/core/services/apiService";

export type State = { classes: object };
import {Module, VuexModule, Mutation, Action} from "vuex-module-decorators";
import {Mutations} from "@/store/enums/store.enums";
import {LicensesActions, LicensesMutation} from "@/modules/licenses/store/enums";

@Module
export default class Store extends VuexModule {
    renderKey = 1;
    listParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    listCache = [];
    listTotal = 0;

    // License Modules
    licenseModules = [];

    // System Modules
    systemModulesParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    systemModulesCache = [];
    systemModulesTotal = 0;

    // Dashboard Widgets
    dashboardWidgetsParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    dashboardWidgetsCache = [];
    dashboardWidgetsTotal = 0;

    // Notification Events
    notificationEventsParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    notificationEventsCache = [];
    notificationEventsTotal = 0;

    // Block Categories
    blockCategories = [];

    // System Blocks
    systemBlocksParams = {
        page: 1,
        results: 20,
        search: null,
        sort: [],
        filter: {}
    };
    systemBlocksCache = [];
    systemBlocksTotal = 0;

    // Resolve URLs
    resolveUrlsParams = {
        page: 1,
        results: 20,
        search: null,
        sort: [],
        filter: {}
    };
    resolveUrlsCache = [];
    resolveUrlsTotal = 0;

    get getLicensesRenderKey() {
        return 'licenses-' + this.renderKey;
    }

    get getLicensesLastListResult() {
        return this.listCache;
    }

    get getLicensesLastListTotal() {
        return this.listTotal;
    }

    get getLicensesLastFilterOptions() {
        return [];
    }

    get getLicensesLicenseModules() {
        return this.licenseModules;
    }

    get getLicensesSystemModulesResult() {
        return this.systemModulesCache;
    }

    get getLicensesSystemModulesTotal() {
        return this.systemModulesTotal;
    }

    get getLicensesDashboardWidgetsResult() {
        return this.dashboardWidgetsCache;
    }

    get getLicensesDashboardWidgetsTotal() {
        return this.dashboardWidgetsTotal;
    }

    get getLicensesNotificationEventsResult() {
        return this.notificationEventsCache;
    }

    get getLicensesNotificationEventsTotal() {
        return this.notificationEventsTotal;
    }

    get getLicensesBlockCategories() {
        return this.blockCategories;
    }

    get getLicensesSystemBlocksResult() {
        return this.systemBlocksCache;
    }

    get getLicensesSystemBlocksTotal() {
        return this.systemBlocksTotal;
    }

    get getLicensesResolveUrlsResult() {
        return this.resolveUrlsCache;
    }

    get getLicensesResolveUrlsTotal() {
        return this.resolveUrlsTotal;
    }

    @Mutation
    [LicensesMutation.SET_RENDER_UPDATE]() {
        this.renderKey++;
    }

    @Mutation
    [LicensesMutation.SET_LIST_RESULT]({listParams, listTotal, listCache}) {
        this.listParams = listParams;
        this.listCache = listCache;
        this.listTotal = listTotal;
    }

    @Mutation
    [LicensesMutation.SET_LICENSE_MODULES](modules) {
        this.licenseModules = modules;
    }

    @Mutation
    [LicensesMutation.SET_SYSTEM_MODULES_RESULT]({listParams, listTotal, listCache}) {
        this.systemModulesParams = listParams;
        this.systemModulesCache = listCache;
        this.systemModulesTotal = listTotal;
    }

    @Mutation
    [LicensesMutation.SET_DASHBOARD_WIDGETS_RESULT]({listParams, listTotal, listCache}) {
        this.dashboardWidgetsParams = listParams;
        this.dashboardWidgetsCache = listCache;
        this.dashboardWidgetsTotal = listTotal;
    }

    @Mutation
    [LicensesMutation.SET_NOTIFICATION_EVENTS_RESULT]({listParams, listTotal, listCache}) {
        this.notificationEventsParams = listParams;
        this.notificationEventsCache = listCache;
        this.notificationEventsTotal = listTotal;
    }

    @Mutation
    [LicensesMutation.SET_BLOCK_CATEGORIES_RESULT](categories) {
        this.blockCategories = categories;
    }

    @Mutation
    [LicensesMutation.SET_SYSTEM_BLOCKS_RESULT]({listParams, listTotal, listCache}) {
        this.systemBlocksParams = listParams;
        this.systemBlocksCache = listCache;
        this.systemBlocksTotal = listTotal;
    }

    @Mutation
    [LicensesMutation.SET_RESOLVE_URLS_RESULT]({listParams, listTotal, listCache}) {
        this.resolveUrlsParams = listParams;
        this.resolveUrlsCache = listCache;
        this.resolveUrlsTotal = listTotal;
    }

    @Action
    [LicensesActions.REFRESH_LIST]() {
        this.context.dispatch(LicensesActions.FETCH_LIST, this.listParams);
    }

    @Action
    [LicensesActions.FETCH_LIST](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/list", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results into storage for reactive update if needed //
                this.context.commit(LicensesMutation.SET_LIST_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.GET_ITEM](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`licenses/get/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {data} = payload;

            apiService.post('licenses/create/', data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // flag cached keep alive comp to render new //
                this.context.commit(LicensesMutation.SET_RENDER_UPDATE);

                // refresh list in background//
                this.context.dispatch(LicensesActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.put(`licenses/edit/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(LicensesActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_ITEM](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(LicensesActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // License Modules Actions //

    @Action
    [LicensesActions.FETCH_LICENSE_MODULES](payload) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/license-modules", payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_LICENSE_MODULES, data.result);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.SAVE_LICENSE_MODULES](payload) {

        return new Promise((resolve, reject) => {

            apiService.post("licenses/save-license-modules", payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // System Modules Actions //

    @Action
    [LicensesActions.REFRESH_SYSTEM_MODULES]() {
        this.context.dispatch(LicensesActions.FETCH_SYSTEM_MODULES, this.systemModulesParams);
    }

    @Action
    [LicensesActions.FETCH_SYSTEM_MODULES](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/system-modules", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_SYSTEM_MODULES_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.GET_SYSTEM_MODULE](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`licenses/get-system-module/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_SYSTEM_MODULE](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('licenses/create-system-module', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_RENDER_UPDATE);
                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_MODULES);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_SYSTEM_MODULE](payload) {

        return new Promise((resolve, reject) => {

            const { editId, data } = payload;

            apiService.put(`licenses/update-system-module/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_MODULES);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_SYSTEM_MODULE](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete-system-module/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_MODULES);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.REORDER_SYSTEM_MODULES](items) {

        return new Promise((resolve, reject) => {

            apiService.post("licenses/reorder-system-modules", { items } as any).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_MODULES);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // Dashboard Widgets Actions //

    @Action
    [LicensesActions.REFRESH_DASHBOARD_WIDGETS]() {
        this.context.dispatch(LicensesActions.FETCH_DASHBOARD_WIDGETS, this.dashboardWidgetsParams);
    }

    @Action
    [LicensesActions.FETCH_DASHBOARD_WIDGETS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/dashboard-widgets", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_DASHBOARD_WIDGETS_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.GET_DASHBOARD_WIDGET](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`licenses/get-dashboard-widget/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_DASHBOARD_WIDGET](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('licenses/create-dashboard-widget', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_RENDER_UPDATE);
                this.context.dispatch(LicensesActions.REFRESH_DASHBOARD_WIDGETS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_DASHBOARD_WIDGET](payload) {

        return new Promise((resolve, reject) => {

            const { editId, data } = payload;

            apiService.put(`licenses/update-dashboard-widget/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_DASHBOARD_WIDGETS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_DASHBOARD_WIDGET](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete-dashboard-widget/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_DASHBOARD_WIDGETS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // Notification Events Actions //

    @Action
    [LicensesActions.REFRESH_NOTIFICATION_EVENTS]() {
        this.context.dispatch(LicensesActions.FETCH_NOTIFICATION_EVENTS, this.notificationEventsParams);
    }

    @Action
    [LicensesActions.FETCH_NOTIFICATION_EVENTS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/notification-events", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_NOTIFICATION_EVENTS_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.GET_NOTIFICATION_EVENT](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`licenses/get-notification-event/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_NOTIFICATION_EVENT](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('licenses/create-notification-event', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_RENDER_UPDATE);
                this.context.dispatch(LicensesActions.REFRESH_NOTIFICATION_EVENTS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_NOTIFICATION_EVENT](payload) {

        return new Promise((resolve, reject) => {

            const { editId, data } = payload;

            apiService.put(`licenses/update-notification-event/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_NOTIFICATION_EVENTS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_NOTIFICATION_EVENT](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete-notification-event/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_NOTIFICATION_EVENTS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // Role Identifiers //

    @Action
    [LicensesActions.FETCH_ROLE_IDENTIFIERS]() {

        return new Promise((resolve, reject) => {

            apiService.get('licenses/role-identifiers').then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // Internal Users //

    @Action
    [LicensesActions.FETCH_INTERNAL_USERS]() {

        return new Promise((resolve, reject) => {

            apiService.get('licenses/internal-users').then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // API Registry //

    @Action
    [LicensesActions.FETCH_API_REGISTRY]() {

        return new Promise((resolve, reject) => {

            apiService.get('licenses/api-registry').then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // Block Categories Actions //

    @Action
    [LicensesActions.FETCH_BLOCK_CATEGORIES]() {

        return new Promise((resolve, reject) => {

            apiService.get("licenses/block-categories").then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_BLOCK_CATEGORIES_RESULT, data.result || []);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_BLOCK_CATEGORY](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('licenses/create-block-category', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.FETCH_BLOCK_CATEGORIES);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_BLOCK_CATEGORY](payload) {

        return new Promise((resolve, reject) => {

            const { editId, data } = payload;

            apiService.put(`licenses/update-block-category/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.FETCH_BLOCK_CATEGORIES);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_BLOCK_CATEGORY](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete-block-category/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.FETCH_BLOCK_CATEGORIES);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // System Blocks Actions //

    @Action
    [LicensesActions.REFRESH_SYSTEM_BLOCKS]() {
        this.context.dispatch(LicensesActions.FETCH_SYSTEM_BLOCKS, this.systemBlocksParams);
    }

    @Action
    [LicensesActions.FETCH_SYSTEM_BLOCKS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/system-blocks", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_SYSTEM_BLOCKS_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.GET_SYSTEM_BLOCK](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`licenses/get-system-block/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_SYSTEM_BLOCK](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('licenses/create-system-block', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_RENDER_UPDATE);
                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_BLOCKS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_SYSTEM_BLOCK](payload) {

        return new Promise((resolve, reject) => {

            const { editId, data } = payload;

            apiService.put(`licenses/update-system-block/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_BLOCKS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_SYSTEM_BLOCK](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete-system-block/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_SYSTEM_BLOCKS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // Resolve URLs Actions //

    @Action
    [LicensesActions.REFRESH_RESOLVE_URLS]() {
        this.context.dispatch(LicensesActions.FETCH_RESOLVE_URLS, this.resolveUrlsParams);
    }

    @Action
    [LicensesActions.FETCH_RESOLVE_URLS](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("licenses/resolve-urls", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_RESOLVE_URLS_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.GET_RESOLVE_URL](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`licenses/get-resolve-url/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.CREATE_RESOLVE_URL](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('licenses/create-resolve-url', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(LicensesMutation.SET_RENDER_UPDATE);
                this.context.dispatch(LicensesActions.REFRESH_RESOLVE_URLS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.UPDATE_RESOLVE_URL](payload) {

        return new Promise((resolve, reject) => {

            const { editId, data } = payload;

            apiService.put(`licenses/update-resolve-url/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_RESOLVE_URLS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DELETE_RESOLVE_URL](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`licenses/delete-resolve-url/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(LicensesActions.REFRESH_RESOLVE_URLS);

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // DEV DB Sync //

    @Action
    [LicensesActions.DEV_SYNC_INFO]() {

        return new Promise((resolve, reject) => {

            apiService.get("licenses/dev-sync-info").then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DEV_SYNC_TRIGGER](params) {

        return new Promise((resolve, reject) => {

            apiService.post("licenses/sync-dev-db", params).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [LicensesActions.DEV_SYNC_STATUS](jobId) {

        return new Promise((resolve, reject) => {

            apiService.get("licenses/dev-sync-status/" + jobId).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

}
